<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.SMALL">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <v-row class="pb-1">
                    <v-col class="py-0" cols="12">
                        <v-btn
                            color="blue-grey"
                            :disabled="disableBackward || loading"
                            icon="mdi-chevron-left"
                            variant="text"
                            @click="previousDay"
                        />
                        <span class="text-subtitle-2">{{ currentDateLabel }}</span>
                        <v-btn
                            color="blue-grey"
                            :disabled="disableForward || loading"
                            icon="mdi-chevron-right"
                            variant="text"
                            @click="nextDay"
                        />
                    </v-col>
                </v-row>
                <v-progress-linear v-if="loading" class="my-2" color="primary" indeterminate />
                <v-data-table v-else :headers="headers" :items="rows" density="compact" hide-default-footer item-value="shipper">
                    <template #body.append>
                        <tr>
                            <td />
                            <td class="font-weight-bold">{{ totalAmount }}</td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { addDays, format, isToday, subDays } from 'date-fns'
import { computed, ref } from 'vue'

import { errorHandler } from '@/composables/errorHandler'
import { useDashboardStore } from '@/stores'
import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import type { DataTableHeader } from '@/types/dataTable'
import { DASHBOARD_SHIPPERS } from '@/views/Dashboard/dashboardConstants'
import { DASHBOARD_WIDGET_COLUMNS } from '@/views/Dashboard/dashboardUtils'

type ShipperRow = { shipper?: string; amount?: unknown }

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 3. Constants
const headers: DataTableHeader[] = [
    { title: 'Nazwa', key: 'shipper', sortable: false },
    { title: 'Ilość', key: 'amount', sortable: false }
]

// 4. Stores & top-level services
const dashboardStore = useDashboardStore()

// 5. Reactive state (refs/reactive)
const currentDate = ref(new Date())
const bodyContent = props.widget?.body?.content
const content = ref<ShipperRow[]>(Array.isArray(bodyContent) ? (bodyContent as ShipperRow[]) : [])
const dayOffset = ref(0)
const loading = ref(false)

// 7. Computed
const currentDateLabel = computed(() => format(currentDate.value, 'dd.MM.yyyy'))
const disableForward = computed(() => isToday(currentDate.value))
const disableBackward = computed(() => dayOffset.value >= 30)
const rows = computed(() =>
    DASHBOARD_SHIPPERS.map(({ code, name }) => ({
        shipper: name,
        amount: Number(content.value.find((item: ShipperRow) => item?.shipper === code)?.amount || 0)
    }))
)
const totalAmount = computed(() => rows.value.reduce((sum, item) => sum + item.amount, 0))

// 8. Handlers / methods
function previousDay(): void {
    dayOffset.value += 1
    currentDate.value = subDays(currentDate.value, 1)
    fetchData()
}

function nextDay(): void {
    dayOffset.value -= 1
    currentDate.value = addDays(currentDate.value, 1)
    fetchData()
}

async function fetchData(): Promise<void> {
    loading.value = true
    try {
        const response = await dashboardStore.getDashboardItem({
            item: props.widget,
            paginate: '',
            query: `sent_date=${format(currentDate.value, 'yyyy-MM-dd')}`
        })
        const next = response?.data?.body?.content
        content.value = Array.isArray(next) ? (next as ShipperRow[]) : []
    } catch (error) {
        errorHandler(error)
    } finally {
        loading.value = false
    }
}
</script>
